import { Link } from "react-router-dom";
import { SiteHeader, SiteFooter } from "../components/SiteLayout";
import { LEGAL } from "../lib/legal";
import SEO from "../components/SEO";

export default function Privacidad() {
  return (
    <div className="min-h-screen bg-stone-50">
      <SEO title="Política de Privacidad" description="Información sobre el tratamiento de datos personales en fmsubastas.com conforme al RGPD y la LOPDGDD." path="/politica-privacidad" />
      <SiteHeader />
      <article className="max-w-3xl mx-auto px-6 py-14 prose-fm">
        <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-3">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#2d1f1a] mb-8">Política de Privacidad</h1>

        <P>De conformidad con el Reglamento (UE) 2016/679, General de Protección de Datos (<strong>RGPD</strong>) y la Ley Orgánica 3/2018, de 5 de diciembre, de Protección de Datos Personales y garantía de los derechos digitales (<strong>LOPDGDD</strong>), se informa a los usuarios sobre el tratamiento de sus datos personales.</P>

        <H2>1. Responsable del tratamiento</H2>
        <Table rows={[
          ["Responsable", LEGAL.ownerName],
          ["NIF", LEGAL.taxId],
          ["Dirección", LEGAL.address],
          ["Correo de contacto en materia de datos", LEGAL.email],
        ]} />

        <H2>2. Finalidades del tratamiento</H2>
        <P>Los datos personales que el Usuario nos facilita serán tratados con las siguientes finalidades:</P>
        <ul className="list-disc pl-6 space-y-1 text-stone-700 mb-4">
          <li>Gestionar las consultas, solicitudes y contactos recibidos a través del formulario de contacto, email, teléfono o WhatsApp.</li>
          <li>Prestar los servicios contratados (Orientación, Consultoría de Deudas, Informe Premium y Gestión Post-Subasta).</li>
          <li>Emitir las facturas correspondientes y cumplir con las obligaciones contables y fiscales.</li>
          <li>Comunicar al Usuario, por correo electrónico o WhatsApp, novedades, plazos y actualizaciones relativas a los servicios contratados.</li>
          <li>Análisis estadístico del uso del sitio web mediante cookies analíticas, previa obtención del consentimiento (ver <Link to="/politica-cookies">Política de Cookies</Link>).</li>
        </ul>

        <H2>3. Base jurídica</H2>
        <P>Las bases legitimadoras del tratamiento son:</P>
        <ul className="list-disc pl-6 space-y-1 text-stone-700 mb-4">
          <li><strong>Consentimiento</strong> expreso del Usuario (art. 6.1.a RGPD) para envío de consultas, suscripción a comunicaciones y uso de cookies no esenciales.</li>
          <li><strong>Ejecución de un contrato</strong> o medidas precontractuales (art. 6.1.b RGPD) para la prestación de los servicios contratados.</li>
          <li><strong>Obligación legal</strong> (art. 6.1.c RGPD) para el cumplimiento de obligaciones fiscales, contables y mercantiles.</li>
          <li><strong>Interés legítimo</strong> (art. 6.1.f RGPD) para la seguridad del sitio (registro de IPs en intentos de acceso fallidos al panel admin) y la prevención del fraude.</li>
        </ul>

        <H2>4. Plazos de conservación</H2>
        <ul className="list-disc pl-6 space-y-1 text-stone-700 mb-4">
          <li>Datos de contactos sin contratación: hasta 24 meses desde el último contacto o hasta que el Usuario solicite su supresión.</li>
          <li>Datos derivados de la prestación de servicios: durante la vigencia de la relación y, posteriormente, durante los plazos legales (mínimo 6 años para facturación, conforme al Código de Comercio y la Ley General Tributaria).</li>
          <li>Documentación registral aportada (nota simple): se conserva como soporte del informe entregado durante 5 años para atención de reclamaciones, transcurridos los cuales se suprime.</li>
          <li>Datos para fines comerciales (con consentimiento): hasta la revocación del consentimiento.</li>
        </ul>

        <H2>5. Destinatarios y encargados del tratamiento</H2>
        <P>El responsable no cederá los datos a terceros salvo obligación legal. Para la prestación del servicio se utilizan los siguientes encargados del tratamiento, todos con garantías RGPD y cláusulas contractuales vigentes:</P>
        <ul className="list-disc pl-6 space-y-1 text-stone-700 mb-4">
          <li><strong>Stripe Payments Europe Ltd.</strong> (Irlanda) — procesamiento de pagos electrónicos.</li>
          <li><strong>Resend (Resend.com)</strong> — envío de correos electrónicos transaccionales (factura, confirmaciones, recordatorios).</li>
          <li><strong>MongoDB Atlas</strong> y/o proveedor de hosting equivalente (UE) — almacenamiento de los datos del sitio.</li>
          <li><strong>Calendly LLC</strong> — gestión de citas para sesiones de orientación.</li>
          <li><strong>Google LLC</strong> — herramientas analíticas (Google Analytics) si el Usuario presta consentimiento mediante el banner de cookies.</li>
        </ul>
        <P>Puede solicitar la lista actualizada de encargados escribiendo a {LEGAL.email}.</P>

        <H2>6. Transferencias internacionales</H2>
        <P>Algunos de los encargados (Stripe, Resend, Google) pueden tratar datos fuera del Espacio Económico Europeo. En todos los casos se cuenta con Cláusulas Contractuales Tipo aprobadas por la Comisión Europea o decisiones de adecuación que garantizan un nivel equivalente de protección.</P>

        <H2>7. Derechos del Usuario</H2>
        <P>En cualquier momento, el Usuario podrá ejercer los siguientes derechos:</P>
        <ul className="list-disc pl-6 space-y-1 text-stone-700 mb-4">
          <li><strong>Acceso</strong> a los datos personales que tratamos sobre él.</li>
          <li><strong>Rectificación</strong> de los datos inexactos.</li>
          <li><strong>Supresión</strong> (<em>derecho al olvido</em>) cuando los datos ya no sean necesarios.</li>
          <li><strong>Limitación</strong> u <strong>oposición</strong> al tratamiento.</li>
          <li><strong>Portabilidad</strong>: solicitar la entrega de los datos en formato estructurado.</li>
          <li><strong>Revocación del consentimiento</strong> previamente prestado, en cualquier momento, sin efecto retroactivo.</li>
          <li><strong>Reclamar ante la Agencia Española de Protección de Datos</strong> (AEPD): <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer">www.aepd.es</a>.</li>
        </ul>
        <P>Para ejercer cualquiera de estos derechos, el Usuario puede dirigirse a <strong>{LEGAL.email}</strong> identificándose adecuadamente. La respuesta se proporcionará en el plazo máximo de 1 mes desde la recepción de la solicitud.</P>

        <H2>8. Medidas de seguridad</H2>
        <P>Se han implementado las medidas técnicas y organizativas adecuadas para garantizar la confidencialidad, integridad, disponibilidad y resiliencia de los sistemas de tratamiento, conforme al art. 32 RGPD: cifrado TLS en todas las comunicaciones, hashing de contraseñas (bcrypt), control de acceso por roles, registro de intentos de acceso, copias de seguridad periódicas, bloqueo automático tras intentos fallidos de login y formación continua del responsable.</P>

        <H2>9. Doble opt-in y comunicaciones comerciales</H2>
        <P>El envío de comunicaciones comerciales (newsletter, promociones) solo se realizará si el Usuario lo ha solicitado expresamente marcando la casilla correspondiente en el formulario. Se aplica un sistema de <strong>doble confirmación (double opt-in)</strong>: tras el alta se envía un correo de verificación que el Usuario debe confirmar para activar la suscripción. En cualquier momento puede darse de baja a través del enlace incluido en cada comunicación o escribiendo a {LEGAL.email}.</P>

        <H2>10. Menores de edad</H2>
        <P>Este sitio no está dirigido a menores de 14 años. El titular no recoge conscientemente datos personales de menores. Si un padre, madre o tutor tuviera conocimiento de que un menor nos ha facilitado datos sin su consentimiento, puede contactarnos para suprimirlos.</P>

        <H2>11. Modificaciones</H2>
        <P>Esta política puede actualizarse para adaptarse a cambios legislativos o de la actividad. Se notificarán los cambios sustanciales mediante aviso destacado en el sitio web.</P>

        <p className="text-xs text-stone-500 mt-12">Última actualización: {LEGAL.lastUpdated}</p>
      </article>
      <SiteFooter />
      <ProseStyle />
    </div>
  );
}

function H2({ children }) { return <h2 className="font-display text-2xl font-black tracking-tight text-[#2d1f1a] mt-12 mb-4">{children}</h2>; }
function P({ children }) { return <p className="text-stone-700 leading-relaxed mb-4">{children}</p>; }
function Table({ rows }) {
  return (
    <div className="bg-white border border-stone-200 rounded-sm overflow-hidden my-6">
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} className="border-b border-stone-200 last:border-0">
              <td className="px-4 py-3 text-stone-500 font-bold text-[10px] uppercase tracking-[0.2em] w-1/3">{k}</td>
              <td className="px-4 py-3 text-stone-800">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function ProseStyle() {
  return <style>{`.prose-fm a{color:#E0651F;text-decoration:underline}.prose-fm a:hover{color:#2d1f1a}`}</style>;
}
